import { css } from '@styled-system/css';
import { App, message as messageStatic } from 'antd';
import {
  ArrowLeft,
  CreditCard,
  Crown,
  Radar,
  Rocket,
  ScanSearch,
  Search,
  ShieldCheck,
  Sparkles,
  Sprout,
  Store,
  TrendingUp,
  XCircle,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import amplitude from '@/amplitude';
import { AuthGuard } from '@/features/auth/components';
import { CreditUsagePanel } from '@/features/credits/CreditUsagePanel';
import { BillingPeriodToggle } from '@/features/subscription/components/BillingPeriodToggle';
import { type PlanAccent, SubscriptionPlanCard } from '@/features/subscription/components/SubscriptionPlanCard';
import { SubscriptionPlanCardSkeleton } from '@/features/subscription/components/SubscriptionPlanCardSkeleton';
import { useSubscriptionData, useSubscriptionSubscribeRedirect } from '@/features/subscription/hooks';
import { buildSharedFeatureRows } from '@/features/subscription/subscriptionPlanFeatures';
import type { BillingPeriod, SubscriptionPlanData } from '@/features/subscription/types';
import { PlatformLogo } from '@/shared/components/analysis/PlatformLogo';
import { Button } from '@/shared/components/ui/Button';
import { Modal } from '@/shared/components/ui/Modal';
import { Spinner } from '@/shared/components/ui/Spinner';

// ============================================================================
// Page Abonnement — structure Autocop : plein écran sur le canvas, bouton
// Retour ghost, hero centré + toggle pill, grille de cards de plans avec
// accents par tier, bandeau de confiance en bas.
// ============================================================================

const page = css({
  minHeight: '100vh',
  bg: 'surface.canvas',
  paddingY: { base: '8', md: '14' },
  paddingX: { base: '4', md: '10' },
});

const container = css({
  maxWidth: '1180px',
  marginX: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: { base: '10', md: '12' },
});

const topBar = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
});

const hero = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '4',
  textAlign: 'center',
});

const heroTitle = css({
  fontSize: { base: 'h1', md: 'display' },
  fontWeight: 'bold',
  color: 'text.primary',
  letterSpacing: '-1.5px',
  lineHeight: '1.1',
  maxWidth: '640px',
});

const heroSubtitle = css({
  fontSize: 'body',
  color: 'text.secondary',
  lineHeight: 'body',
  maxWidth: '540px',
});

const toggleWrap = css({ marginTop: '2' });

const grid = css({
  display: 'grid',
  // 1 colonne mobile · 2x2 tablette/petit laptop · 4 colonnes grand écran.
  gridTemplateColumns: { base: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)' },
  gap: '6',
  alignItems: 'stretch',
  width: '100%',
});

const trustStrip = css({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'center',
  gap: { base: '4', md: '8' },
  color: 'text.tertiary',
  fontSize: 'bodySm',
});

const trustItem = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '2',
});

// Bandeau « essai gratuit en cours » — au-dessus du hero pendant l'essai.
const trialBanner = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: '5',
  paddingY: '5',
  paddingX: '6',
  borderRadius: 'xl',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'brand.primary/40',
  backgroundImage: 'linear-gradient(135deg, token(colors.brand.primarySoft) 0%, token(colors.surface.elevated) 70%)',
  boxShadow: '0 18px 44px -24px color-mix(in srgb, token(colors.brand.primary) 45%, transparent)',
});

const trialBannerCta = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: { base: 'stretch', md: 'flex-end' },
  gap: '2',
  flexShrink: 0,
  minWidth: '260px',
});

const trialBannerIcon = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '36px',
  height: '36px',
  borderRadius: 'lg',
  bg: 'brand.primary/10',
  color: 'brand.primary',
  flexShrink: 0,
});

const trialBannerTitle = css({ fontSize: 'body', fontWeight: 'semibold', color: 'text.primary' });

const trialBannerHint = css({ fontSize: 'bodySm', color: 'text.secondary', lineHeight: 1.55 });

const switchModalBody = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '4',
  fontSize: 'bodySm',
  color: 'text.secondary',
  lineHeight: 1.6,
});

const TRUST_ITEMS = [
  { icon: <ShieldCheck size={16} strokeWidth={1.75} />, labelKey: 'subscriptionPage.trustStripe' },
  { icon: <XCircle size={16} strokeWidth={1.75} />, labelKey: 'subscriptionPage.trustCancelAnytime' },
  { icon: <CreditCard size={16} strokeWidth={1.75} />, labelKey: 'subscriptionPage.trustNoCommitment' },
];

// Veilles simultanées par plan (spec pricing 2026-08-18) — le serveur n'expose
// pas (encore) veilleQuotas sur SubscriptionPlan, la valeur est portée ici.
const VEILLE_QUOTAS: Record<string, number> = {
  'recop-beginner': 3,
  'recop-classic': 15,
  'recop-pro': 50,
};

// Icône + accent par tier, même logique que les cards de pricing Autocop.
const PLAN_META: Record<string, { icon: React.ReactNode; accent: PlanAccent }> = {
  beginner: { icon: <Sprout size={24} strokeWidth={1.75} />, accent: 'neutral' },
  classic: { icon: <TrendingUp size={24} strokeWidth={1.75} />, accent: 'brand' },
  pro: { icon: <Rocket size={24} strokeWidth={1.75} />, accent: 'green' },
  expert: { icon: <Crown size={24} strokeWidth={1.75} />, accent: 'neutral' },
};

export function SubscriptionPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Touche Escape → retour à l'app
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') navigate('/');
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [navigate]);

  // Track subscription success with Amplitude if ?success=true in URL
  const isSubscriptionSuccess = searchParams.get('success') === 'true';
  useEffect(() => {
    if (isSubscriptionSuccess) {
      amplitude.track('Paiement Completed');
    }
  }, [isSubscriptionSuccess]);

  // Période de facturation : URL prioritaire, sinon celle de l'abonnement
  const getInitialBillingPeriod = (): BillingPeriod =>
    searchParams.get('billing_period') === 'yearly' ? 'yearly' : 'monthly';
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>(getInitialBillingPeriod);

  const {
    subscriptionPlans,
    loading,
    currentSubscriptionPeriodicity,
    canStartFreeTrial,
    isTrialing,
    trialDaysLeft,
    selectPlanDuringTrial,
  } = useSubscriptionData(billingPeriod);

  // Toasts (repli statique hors <App>)
  let appMessage: typeof messageStatic;
  try {
    appMessage = App.useApp().message as unknown as typeof messageStatic;
  } catch {
    appMessage = messageStatic;
  }

  // Popup de confirmation de bascule pendant l'essai
  const [switchTarget, setSwitchTarget] = useState<SubscriptionPlanData | null>(null);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    const billingPeriodParam = searchParams.get('billing_period');
    if (billingPeriodParam) {
      setBillingPeriod(billingPeriodParam === 'yearly' ? 'yearly' : 'monthly');
    } else if (currentSubscriptionPeriodicity) {
      setBillingPeriod(currentSubscriptionPeriodicity);
    }
  }, [searchParams, currentSubscriptionPeriodicity]);

  // Redirection de souscription pilotée par l'URL (?subscribe=...)
  const { isLoading: isSubscriptionRedirectLoading } = useSubscriptionSubscribeRedirect({
    subscriptionPlans,
    subscriptionPlansLoading: loading,
  });

  if (isSubscriptionRedirectLoading) {
    return (
      <div
        className={css({
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100vw',
          height: '100vh',
          bg: 'surface.canvas',
        })}
      >
        <Spinner size={32} />
      </div>
    );
  }

  const getPlanPrice = (plan: NonNullable<typeof subscriptionPlans>['customPlans'][number]) => {
    if (billingPeriod === 'yearly' && plan.priceYearly) {
      return {
        monthlyPrice: Math.round((plan.priceYearly / 12) * 100) / 100,
        baseMonthlyPrice: plan.priceMonthly,
      };
    }
    return { monthlyPrice: plan.priceMonthly, baseMonthlyPrice: undefined };
  };

  // Features par tier, valeurs embarquées dans le label (idiome Autocop).
  // Quotas produit/vendeur MENSUELS (serveur), veilles simultanées (VEILLE_QUOTAS).
  // Ordre (produit, vendeur, veilles?, Vestiaire, image) décidé par la fonction
  // pure `buildSharedFeatureRows` (testée séparément) — ici, seul le mapping
  // vers icône/libellé JSX.
  const sharedFeatures = (plan?: {
    id?: string;
    catalogAnalysisQuotas?: number | string;
    profileAnalysisQuotas?: number | string;
    vestiaireAccess?: boolean;
  }) => {
    const veilleQuota = plan?.id != null ? VEILLE_QUOTAS[plan.id] : undefined;
    return buildSharedFeatureRows({ veilleQuota, vestiaireAccess: plan?.vestiaireAccess }).map((row) => {
      switch (row.kind) {
        case 'productSearches':
          return {
            icon: <Search size={15} strokeWidth={2} />,
            label: t('subscriptionPage.featureProductSearches', { value: plan?.catalogAnalysisQuotas ?? '–' }),
          };
        case 'vendorSearches':
          return {
            icon: <Store size={15} strokeWidth={2} />,
            label: t('subscriptionPage.featureVendorSearches', { value: plan?.profileAnalysisQuotas ?? '–' }),
          };
        case 'veilles':
          return {
            icon: <Radar size={15} strokeWidth={2} />,
            label: t('subscriptionPage.featureVeilles', { value: veilleQuota }),
          };
        case 'vestiaire':
          // `tileIcon` (pas `icon`) : pastille dédiée, prioritaire sur le check par défaut — le
          // composant bascule seul sur une croix grisée quand `excluded` est vrai.
          return {
            tileIcon: <PlatformLogo platform="vestiaire" size={16} />,
            label: t('subscriptionPage.featureVestiaire'),
            excluded: row.excluded,
          };
        case 'image':
          return { icon: <ScanSearch size={15} strokeWidth={2} />, label: t('subscriptionPage.featureImageUnlimited') };
      }
    });
  };

  // Pendant l'essai : le CTA des autres plans ouvre la confirmation de
  // bascule directe (fin d'essai + débit) au lieu du checkout Stripe.
  const trialCardProps = (plan?: SubscriptionPlanData) =>
    isTrialing && plan && !plan.isCurrentPlan
      ? {
          trialSwitchMode: true,
          handleSubscribe: async () => {
            setSwitchTarget(plan);
          },
        }
      : {};

  const handleConfirmSwitch = async () => {
    if (!switchTarget) return;
    setSwitching(true);
    try {
      await selectPlanDuringTrial(switchTarget.id, billingPeriod);
      appMessage.success(t('subscriptionPage.switchSuccess', { name: switchTarget.name }));
      setSwitchTarget(null);
    } catch (error) {
      // Erreurs serveur en clair (ex. « Le paiement a été refusé. Votre essai continue. »)
      appMessage.error(error instanceof Error ? error.message : t('subscriptionPage.switchError'));
    } finally {
      setSwitching(false);
    }
  };

  const switchPrice = switchTarget
    ? billingPeriod === 'yearly' && switchTarget.priceYearly
      ? t('subscriptionPage.pricePerYear', { price: switchTarget.priceYearly.toFixed(2) })
      : t('subscriptionPage.pricePerMonth', { price: switchTarget.priceMonthly.toFixed(2) })
    : '';

  // Note « quotas de l'essai » sur la card qui porte l'essai (Débutant)
  const beginnerTrialNote =
    canStartFreeTrial &&
    !isTrialing &&
    (subscriptionPlans?.beginner.trialPeriodDays ?? 0) > 0 &&
    subscriptionPlans?.beginner.trialCatalogAnalysisQuotas != null
      ? t('subscriptionPage.beginnerTrialNote', {
          product: subscriptionPlans.beginner.trialCatalogAnalysisQuotas,
          vendor: subscriptionPlans.beginner.trialProfileAnalysisQuotas,
          veille: subscriptionPlans.beginner.trialVeilleQuotas,
        })
      : undefined;

  const EXPERT_FEATURES = [
    { icon: <Search size={15} strokeWidth={2} />, label: t('subscriptionPage.expertProductUnlimited') },
    { icon: <Store size={15} strokeWidth={2} />, label: t('subscriptionPage.expertVendorUnlimited') },
    { icon: <Radar size={15} strokeWidth={2} />, label: t('subscriptionPage.expertVeillesUnlimited') },
    // Toujours incluse (carte codée en dur, jamais limitée par vestiaireAccess) — `tileIcon` pour le logo.
    { tileIcon: <PlatformLogo platform="vestiaire" size={16} />, label: t('subscriptionPage.featureVestiaire') },
    { icon: <ScanSearch size={15} strokeWidth={2} />, label: t('subscriptionPage.featureImageUnlimited') },
    { icon: <Crown size={15} strokeWidth={2} />, label: t('subscriptionPage.expertSupport') },
  ];

  return (
    <AuthGuard>
      <div className={page}>
        <div className={container}>
          <div className={topBar}>
            <Button
              variant="ghost"
              size="sm"
              icon={<ArrowLeft size={14} strokeWidth={1.75} />}
              onClick={() => navigate('/')}
            >
              {t('subscriptionPage.back')}
            </Button>
          </div>

          {/* Essai en cours : bandeau CTA bien visible AU-DESSUS des quotas */}
          {isTrialing && (
            <div className={trialBanner}>
              <div className={css({ display: 'flex', alignItems: 'flex-start', gap: '4', flex: 1, minWidth: '280px' })}>
                <span className={trialBannerIcon}>
                  <Sparkles size={17} strokeWidth={1.75} />
                </span>
                <div className={css({ display: 'flex', flexDirection: 'column', gap: '1.5' })}>
                  <span className={trialBannerTitle}>
                    {t('subscriptionPage.trialBannerTitle', { count: trialDaysLeft })}
                  </span>
                  <span className={trialBannerHint}>{t('subscriptionPage.trialBannerHint')}</span>
                </div>
              </div>
              <div className={trialBannerCta}>
                <Button
                  variant="cta"
                  size="lg"
                  onClick={() => {
                    if (subscriptionPlans?.beginner) setSwitchTarget(subscriptionPlans.beginner);
                  }}
                >
                  {t('subscriptionPage.trialBannerCta')}
                </Button>
                <span className={css({ fontSize: 'caption', color: 'text.tertiary', textAlign: 'center' })}>
                  {t('subscriptionPage.trialBannerNote')}
                  {subscriptionPlans?.beginner
                    ? ` ${t('subscriptionPage.trialBannerFrom', {
                        price: subscriptionPlans.beginner.priceMonthly.toFixed(2),
                      })}`
                    : ''}
                </span>
              </div>
            </div>
          )}

          {/* Plan actuel + usage des quotas */}
          <CreditUsagePanel />

          <div className={hero}>
            <h1 className={heroTitle}>{t('subscriptionPage.heroTitle')}</h1>
            <p className={heroSubtitle}>{t('subscriptionPage.heroSubtitle')}</p>
            <div className={toggleWrap}>
              <BillingPeriodToggle
                value={billingPeriod}
                onChange={setBillingPeriod}
                discount="−15%"
                disabled={loading}
              />
            </div>
          </div>

          <div className={grid}>
            {loading ? (
              <>
                <SubscriptionPlanCardSkeleton />
                <SubscriptionPlanCardSkeleton />
                <SubscriptionPlanCardSkeleton />
                <SubscriptionPlanCardSkeleton />
              </>
            ) : (
              <>
                <SubscriptionPlanCard
                  planName={subscriptionPlans?.beginner.name ?? '–'}
                  {...(subscriptionPlans?.beginner ? getPlanPrice(subscriptionPlans.beginner) : {})}
                  freeTrialDays={subscriptionPlans?.beginner.trialPeriodDays ?? 0}
                  subscriptionType="subscribe"
                  features={sharedFeatures(subscriptionPlans?.beginner)}
                  isCurrentPlan={subscriptionPlans?.beginner.isCurrentPlan}
                  isDowngrade={subscriptionPlans?.beginner.isDowngrade}
                  accent={PLAN_META.beginner.accent}
                  icon={PLAN_META.beginner.icon}
                  index={0}
                  trialNote={beginnerTrialNote}
                  isCurrentTrial={isTrialing && subscriptionPlans?.beginner.isCurrentPlan}
                  handleActivateNow={
                    isTrialing && subscriptionPlans?.beginner.isCurrentPlan
                      ? () => setSwitchTarget(subscriptionPlans.beginner)
                      : undefined
                  }
                  handleSubscribe={subscriptionPlans?.beginner.handleSubscribe}
                  handleCancel={subscriptionPlans?.beginner.handleCancel}
                  {...trialCardProps(subscriptionPlans?.beginner)}
                />

                {/* Le plan mis en avant : Classique */}
                <SubscriptionPlanCard
                  planName={subscriptionPlans?.classic.name ?? '–'}
                  {...(subscriptionPlans?.classic ? getPlanPrice(subscriptionPlans.classic) : {})}
                  freeTrialDays={subscriptionPlans?.classic.trialPeriodDays ?? 0}
                  isHighlighted
                  subscriptionType="subscribe"
                  features={sharedFeatures(subscriptionPlans?.classic)}
                  isCurrentPlan={subscriptionPlans?.classic.isCurrentPlan}
                  isDowngrade={subscriptionPlans?.classic.isDowngrade}
                  accent={PLAN_META.classic.accent}
                  icon={PLAN_META.classic.icon}
                  index={1}
                  handleSubscribe={subscriptionPlans?.classic.handleSubscribe}
                  handleCancel={subscriptionPlans?.classic.handleCancel}
                  {...trialCardProps(subscriptionPlans?.classic)}
                />

                <SubscriptionPlanCard
                  planName={subscriptionPlans?.pro.name ?? '–'}
                  {...(subscriptionPlans?.pro ? getPlanPrice(subscriptionPlans.pro) : {})}
                  freeTrialDays={subscriptionPlans?.pro.trialPeriodDays ?? 0}
                  subscriptionType="subscribe"
                  features={sharedFeatures(subscriptionPlans?.pro)}
                  isCurrentPlan={subscriptionPlans?.pro.isCurrentPlan}
                  isDowngrade={subscriptionPlans?.pro.isDowngrade}
                  accent={PLAN_META.pro.accent}
                  icon={PLAN_META.pro.icon}
                  index={2}
                  handleSubscribe={subscriptionPlans?.pro.handleSubscribe}
                  handleCancel={subscriptionPlans?.pro.handleCancel}
                  {...trialCardProps(subscriptionPlans?.pro)}
                />

                <SubscriptionPlanCard
                  planName="Recop Expert"
                  subscriptionType="contact"
                  features={EXPERT_FEATURES}
                  accent={PLAN_META.expert.accent}
                  icon={PLAN_META.expert.icon}
                  index={3}
                  handleContact={() => navigate('/contact')}
                />

                {subscriptionPlans?.customPlans
                  ?.filter(
                    (plan) =>
                      (billingPeriod === 'monthly' && plan.priceMonthly) ||
                      (billingPeriod === 'yearly' && plan.priceYearly),
                  )
                  .map((customPlan, i) => (
                    <SubscriptionPlanCard
                      key={customPlan.id}
                      planName={customPlan.name}
                      {...getPlanPrice(customPlan)}
                      freeTrialDays={customPlan.trialPeriodDays}
                      subscriptionType="subscribe"
                      features={sharedFeatures(customPlan)}
                      isCurrentPlan={customPlan.isCurrentPlan}
                      isDowngrade={customPlan.isDowngrade}
                      accent="neutral"
                      icon={<Sprout size={24} strokeWidth={1.75} />}
                      index={4 + i}
                      handleSubscribe={customPlan.handleSubscribe}
                      handleCancel={customPlan.handleCancel}
                      {...trialCardProps(customPlan)}
                    />
                  ))}
              </>
            )}
          </div>

          {/* Confirmation de bascule directe pendant l'essai */}
          <Modal
            isOpen={switchTarget !== null}
            onClose={() => {
              if (!switching) setSwitchTarget(null);
            }}
            title={
              switchTarget
                ? switchTarget.isCurrentPlan
                  ? t('subscriptionPage.switchTitleActivate', { name: switchTarget.name })
                  : t('subscriptionPage.switchTitleUpgrade', { name: switchTarget.name })
                : ''
            }
            width="440px"
            footer={
              <>
                <Button variant="ghost" onClick={() => setSwitchTarget(null)} disabled={switching}>
                  {t('common.cancel')}
                </Button>
                <Button variant="primary" handleClick={handleConfirmSwitch} showLoader disabled={switching}>
                  {t('subscriptionPage.switchConfirm')}
                </Button>
              </>
            }
          >
            <div className={switchModalBody}>
              <span>
                <Trans
                  i18nKey="subscriptionPage.switchBody1"
                  values={{ price: switchPrice }}
                  components={{ strong: <strong /> }}
                />
              </span>
              <span>{t('subscriptionPage.switchBody2', { name: switchTarget?.name ?? '' })}</span>
            </div>
          </Modal>

          <div className={trustStrip}>
            {TRUST_ITEMS.map((item) => (
              <span key={item.labelKey} className={trustItem}>
                {item.icon}
                {t(item.labelKey)}
              </span>
            ))}
          </div>
        </div>
      </div>
    </AuthGuard>
  );
}
